import * as Phaser from 'phaser';
import type { WhisperGrantedPayload } from '@game/shared';
import { MSG_JOURNAL_WHISPER_GRANTED } from '@game/shared';

const TOAST_HOLD_MS = 3500;
const TOAST_FADE_MS = 800;
const TOAST_BOTTOM_OFFSET = 64;
const TOAST_FONT_SIZE = '15px';
const TOAST_COLOR = '#f5e6c8';

/**
 * The line shown when whispers settle. Exported so the C5 slip bridge can
 * render the same sentence on paper without duplicating the wording.
 */
export function whisperToastMessage(count: number): string {
  return count === 1
    ? 'A new whisper has settled in your journal…'
    : `${count} new whispers have settled in your journal…`;
}

export class WhisperToast {
  private scene: Phaser.Scene;
  private toastText: Phaser.GameObjects.Text | null = null;
  private activeTween: Phaser.Tweens.Tween | null = null;
  private room: { onMessage: (type: string, cb: (payload: unknown) => void) => void } | null;

  constructor(
    scene: Phaser.Scene,
    room: { onMessage: (type: string, cb: (payload: unknown) => void) => void } | null,
  ) {
    this.scene = scene;
    this.room = room;
    this.wireListener();
  }

  private wireListener(): void {
    if (!this.room) return;
    this.room.onMessage(MSG_JOURNAL_WHISPER_GRANTED, (raw: unknown) => {
      const payload = raw as WhisperGrantedPayload;
      if (payload.whisperIds.length === 0) return;
      this.showToast(whisperToastMessage(payload.whisperIds.length));
    });
  }

  private showToast(message: string): void {
    if (this.activeTween) {
      this.activeTween.destroy();
      this.activeTween = null;
    }
    if (this.toastText) {
      this.toastText.destroy();
      this.toastText = null;
    }

    const cam = this.scene.cameras.main;
    const x = cam.width / 2;
    const y = cam.height - TOAST_BOTTOM_OFFSET;

    this.toastText = this.scene.add.text(x, y, message, {
      fontSize: TOAST_FONT_SIZE,
      color: TOAST_COLOR,
      fontFamily: 'serif',
      align: 'center',
      shadow: {
        offsetX: 1,
        offsetY: 1,
        color: '#000000',
        blur: 3,
        fill: true,
        stroke: true,
      },
    });
    this.toastText.setOrigin(0.5, 0.5);
    this.toastText.setAlpha(0);
    this.toastText.setDepth(200);
    this.toastText.setScrollFactor(0);

    this.activeTween = this.scene.tweens.add({
      targets: this.toastText,
      alpha: { from: 0, to: 1 },
      duration: TOAST_FADE_MS,
      ease: 'Sine.easeIn',
      hold: TOAST_HOLD_MS,
      yoyo: true,
      onComplete: () => {
        this.toastText?.destroy();
        this.toastText = null;
        this.activeTween = null;
      },
    });
  }

  destroy(): void {
    if (this.activeTween) {
      this.activeTween.destroy();
      this.activeTween = null;
    }
    if (this.toastText) {
      this.toastText.destroy();
      this.toastText = null;
    }
    this.room = null;
  }
}
